
import React, { useState } from 'react';

const ServicesSection: React.FC = () => {
  const [activeService, setActiveService] = useState<number | null>(null);

  const services = [
    {
      icon: 'ri-robot-line',
      title: 'AI Agents & Automation',
      description: 'Transform your business operations with intelligent automation and AI-powered agents that work 24/7.',
      gradient: 'from-purple-500 to-pink-600',
      features: [
        {
          name: 'ADK (Agent Development Kit) integration',
          outcome: 'Reduces development time by 60-70% through pre-built components and enables faster time-to-market'
        },
        {
          name: 'MCP (Model Context Protocol) implementation',
          outcome: 'Improves AI accuracy by 40-50% and reduces customer service response time by up to 80%'
        },
        {
          name: 'A2A (Agent-to-Agent) communication',
          outcome: 'Increases operational efficiency by 50-70% and reduces errors in multi-step processes by 90%'
        },
        {
          name: 'LangChain and LangGraph workflows',
          outcome: 'Reduces manual processing time by 75% and improves decision-making accuracy'
        },
        {
          name: 'n8n and Make automation platforms',
          outcome: 'Saves 20-30 hours per week on manual tasks and reduces operational costs by 40-60%'
        }
      ]
    },
    {
      icon: 'ri-eye-line',
      title: 'Observability',
      description: 'Gain complete visibility into your systems with advanced monitoring and analytics solutions.',
      gradient: 'from-blue-500 to-cyan-600',
      features: [
        {
          name: 'Infrastructure monitoring',
          outcome: 'Prevents 95% of potential system failures and reduces downtime costs by $100K-$500K annually'
        },
        {
          name: 'Virtualization monitoring',
          outcome: 'Optimizes resource utilization by 40-50% and reduces infrastructure costs by 25-35%'
        },
        {
          name: 'Cloud provider monitoring',
          outcome: 'Reduces cloud costs by 20-30% through optimization insights and prevents vendor lock-in'
        },
        {
          name: 'Application monitoring and distributed tracing',
          outcome: 'Reduces mean time to resolution (MTTR) by 70% and increases customer satisfaction by 25-40%'
        }
      ]
    },
    {
      icon: 'ri-database-line',
      title: 'Data Engineering & BI',
      description: 'Unlock the power of your data with comprehensive analytics and business intelligence solutions.',
      gradient: 'from-green-500 to-emerald-600',
      features: [
        {
          name: 'Comprehensive data analytics',
          outcome: 'Increases revenue by 15-25% through data-driven decisions and improves customer retention by 20-30%'
        },
        {
          name: 'ETL and ELT pipeline development',
          outcome: 'Reduces data processing time from hours to minutes with 99.9% data accuracy'
        },
        {
          name: 'Data warehouse optimization',
          outcome: 'Reduces query response time by 80-90% and lowers storage costs by 30-50%'
        },
        {
          name: 'Business intelligence dashboards',
          outcome: 'Enables faster decision-making (from days to hours) and increases operational visibility by 100%'
        }
      ]
    },
    {
      icon: 'ri-rocket-line',
      title: 'Rapid MVP Development',
      description: 'Launch your ideas quickly with our accelerated development process and proven methodologies.',
      gradient: 'from-orange-500 to-red-600',
      features: [
        {
          name: 'Full-stack software development',
          outcome: 'Validates business ideas 10x faster and reduces initial investment risk by 70%'
        },
        {
          name: 'AI agent development',
          outcome: 'Creates new revenue streams and reduces operational costs by 40-60%'
        },
        {
          name: '2-week delivery timeline',
          outcome: 'Captures market opportunities before competitors and reduces time-to-revenue by 6-12 months'
        },
        {
          name: 'Production-ready deployment',
          outcome: 'Ensures scalability from day one and enables immediate user acquisition'
        }
      ]
    }
  ];

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-teal-500 to-blue-600 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-4xl mx-auto mb-20">
          <div className="inline-flex items-center px-4 py-2 bg-teal-100 text-teal-800 rounded-full text-sm font-medium mb-6">
            <i className="ri-service-line mr-2"></i>
            Our Core Services
          </div>
          <h2 className="text-5xl md:text-6xl font-bold mb-8 text-gray-900 leading-tight">
            Transforming Ideas Into
            <span className="bg-gradient-to-r from-teal-500 to-blue-600 bg-clip-text text-transparent"> Digital Reality</span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            We deliver cutting-edge solutions that drive measurable business outcomes and accelerate your digital transformation journey.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
          {services.map((service, index) => (
            <div
              key={index}
              className={`group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 ${
                activeService === index ? 'ring-2 ring-teal-500' : ''
              }`}
              style={{
                animationDelay: `${index * 150}ms`,
                animation: 'fadeInUp 0.8s ease-out forwards'
              }}
            >
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-5 rounded-3xl transition-opacity duration-500`}></div>
              
              {/* Icon */}
              <div className={`relative w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <i className={`${service.icon} text-2xl text-white`}></i>
              </div>

              {/* Content */}
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-gray-800 transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Key Features */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <span className={`w-2 h-2 bg-gradient-to-r ${service.gradient} rounded-full mr-3`}></span>
                    Key Features & Business Outcomes
                  </h4>
                  <div className="space-y-4">
                    {service.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="group/feature cursor-pointer"
                        onClick={() => setActiveService(activeService === index ? null : index)}
                      >
                        <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                          <div className={`w-1.5 h-1.5 bg-gradient-to-r ${service.gradient} rounded-full mt-2 flex-shrink-0`}></div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-800 text-sm mb-1">
                              {feature.name}
                            </div>
                            <div className={`text-xs text-gray-600 leading-relaxed transition-all duration-300 ${
                              activeService === index ? 'opacity-100 max-h-20' : 'opacity-70 max-h-0 overflow-hidden'
                            }`}>
                              <strong>Business Impact:</strong> {feature.outcome}
                            </div>
                          </div>
                          <i className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-300 ${
                            activeService === index ? 'rotate-180' : ''
                          }`}></i>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  onClick={scrollToContact}
                  className={`inline-flex items-center text-sm font-semibold bg-gradient-to-r ${service.gradient} bg-clip-text text-transparent hover:opacity-80 transition-opacity duration-300 whitespace-nowrap group/btn`}
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-2 group-hover/btn:translate-x-1 transition-transform duration-300"></i>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA Section */}
        <div className="text-center mt-20">
          <div className="bg-gradient-to-r from-teal-500 to-blue-600 rounded-3xl p-12 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Transform Your Business?
              </h3>
              <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
                Our solutions typically deliver 300-500% ROI within the first year. Let's discuss how we can accelerate your success.
              </p>
              <button
                onClick={scrollToContact}
                className="bg-white text-teal-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors duration-300 shadow-lg whitespace-nowrap"
              >
                Start Your Transformation
              </button>
            </div>
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default ServicesSection;
